/**
 * The rules behind the hold-music control, with no React in them.
 *
 * Three questions, and each of them has an answer that looks right and is not:
 *
 *  - **What should the player load?** Not the field's value. An uploaded track
 *    is stored in the setting as `/api/telephony/hold/<id>`, which serves a
 *    looping TwiML document to Twilio - handing that to an `<audio>` element
 *    downloads a few lines of XML and plays nothing. The audio lives at the clip
 *    address beside it, which is why an upload reports both.
 *  - **Which upload is the field describing?** The one whose id is IN the field,
 *    not the newest one. Matching on the address rather than on recency is what
 *    keeps the description honest when somebody uploads a second track and then
 *    pastes the old address back: the card then describes the old file again.
 *  - **Is this ours at all?** A pasted address is somebody else's file. It can
 *    be played and it must not be described - naming a size and a filename for a
 *    URL nobody here uploaded would be an invention.
 *
 * A fourth was added after a hold track was uploaded in production and never
 * heard by anybody: **is there a file here that nothing is using?** See
 * `strayHoldMusic`.
 */
import type { HoldMusicInfo, StrayHoldMusic, UploadedHoldMusic } from "../../../../lib/calls";

/**
 * Mirrors the API's `HOLD_MUSIC_PREFIX`.
 *
 * The one thing this side needs from that module: telling our own uploads apart
 * from a pasted address. Everything else about the shape (that it loops, that
 * it points at the clip route) is the server's business.
 */
export const HOLD_MUSIC_PREFIX = "/api/telephony/hold/";

/** The clip id inside a hold address, or `null` when this is not one of ours. */
export function holdClipId(url: string): string | null {
  const value = url.trim();
  if (!value.startsWith(HOLD_MUSIC_PREFIX)) return null;
  return value.slice(HOLD_MUSIC_PREFIX.length).trim() || null;
}

/** The field points at a track uploaded here rather than at somebody's URL. */
export function isUploadedHold(url: string): boolean {
  return holdClipId(url) !== null;
}

/**
 * Every track this page knows anything about, passed around as one value.
 *
 * One object rather than three arguments because they are one fact - "what is
 * in the store" - arriving by three routes, and each function below needs a
 * different subset. Threading them separately is how the stray came to be
 * missing from the description lookup on its first draft: the field adopted a
 * track and then had nothing to say about it and nothing to play.
 */
export interface HoldSources {
  /** What the last load said the SAVED setting points at. */
  saved: HoldMusicInfo | null;
  /** What this form uploaded and has not saved. The server has not been told. */
  fresh: UploadedHoldMusic | null;
  /** What the server found in the store with nothing pointing at it. */
  stray: StrayHoldMusic | null;
}

/**
 * The upload the field currently points at, whether it arrived a minute ago in
 * this form, a month ago from somebody else, or from a form nobody saved.
 *
 * All three sources, because the field can legitimately name any of them and a
 * described track is the difference between a player and a bare address.
 */
export function holdInfoFor(
  value: string,
  src: HoldSources,
): HoldMusicInfo | UploadedHoldMusic | StrayHoldMusic | null {
  const id = holdClipId(value);
  if (!id) return null;
  if (src.fresh && src.fresh.id === id) return src.fresh;
  if (src.saved && src.saved.id === id) return src.saved;
  if (src.stray && src.stray.id === id) return src.stray;
  return null;
}

/**
 * What the player loads, or "" for nothing to play.
 *
 * A pasted address plays as it stands - it is already what Twilio would fetch.
 * One of ours plays from the clip address the upload reported, and only when
 * that upload is actually known here: a hold address whose row nobody can
 * describe (uploaded on another machine, or collected) gets no player rather
 * than a broken one.
 */
export function holdPreviewUrl(value: string, src: HoldSources): string {
  const url = value.trim();
  if (!url) return "";
  if (!isUploadedHold(url)) return url;
  return holdInfoFor(url, src)?.audioUrl ?? "";
}

/**
 * A track that exists and that the field is not pointing at, or `null`.
 *
 * WHY THIS IS NOT PARANOIA. Uploading and saving are two presses, and the only
 * thing carrying the address between them is one unsaved field. In production
 * that field was lost between the two - a 2.7 MB track sat in the table for
 * hours while every held caller heard Twilio's classical playlist, and the page
 * showed nothing at all, because an empty field is exactly what "I never
 * uploaded anything" looks like. This is the check that makes the two cases
 * different on screen.
 *
 * Two sources, one answer, because the operator's question is the same either
 * way: `fresh` is what this form uploaded and is the case where the field moved
 * away afterwards; `stray` is what the SERVER found unreferenced and is the
 * case where the form was closed, reloaded, or saved from somewhere else.
 *
 * `fresh` wins when both exist. It is the newer of the two by construction, and
 * it is the one the operator has in mind - they pressed upload a minute ago.
 *
 * Silent whenever the field already points at the track: that is the ordinary
 * unsaved state, which has its own sentence and does not need a warning too.
 */
export function strayHoldMusic(
  value: string,
  src: HoldSources,
): UploadedHoldMusic | StrayHoldMusic | null {
  const id = holdClipId(value);
  if (src.fresh) return src.fresh.id === id ? null : src.fresh;
  if (src.stray) return src.stray.id === id ? null : src.stray;
  return null;
}

/**
 * How long it plays, as "1:04".
 *
 * Minutes and seconds rather than the announcements' one decimal: a notice is
 * judged by whether it is 8 or 10 seconds of a stranger waiting, and a hold loop
 * by whether it is one minute or four. Empty when the length is not known, which
 * is every MP3 - reading one would mean decoding it, and a guess printed beside
 * a real filename reads as a measurement.
 */
export function holdSeconds(durationMs: number | null): string {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0) return "";
  const total = Math.round(durationMs / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
